import React, { useState } from 'react';
import {
  Save, UploadCloud, RotateCcw, Wand2, Building2, ChevronDown
} from 'lucide-react';
import { Button } from '@/shared/components/ui/Button';
import { Badge, type BadgeTone } from '@/shared/components/ui/Badge';
import { type CustomTemplateSummary } from '../api';

const STANDARD_DEPARTMENTS = [
  { label: 'Company-Wide Default (All Departments)', value: '' },
  { label: 'Engineering & Technology', value: 'Engineering' },
  { label: 'Sales & Marketing', value: 'Sales & Marketing' },
  { label: 'Human Resources', value: 'Human Resources' },
  { label: 'Finance & Accounts', value: 'Finance' },
  { label: 'Operations & Admin', value: 'Operations' },
  { label: 'Product & Design', value: 'Product & Design' },
];

interface StatusInfo {
  label: string;
  tone: BadgeTone;
  description: string;
}

function statusFor(summary: CustomTemplateSummary | undefined): StatusInfo {
  if (!summary) {
    return {
      label: 'Using ZYNX Standard',
      tone: 'neutral',
      description: 'Documents generated for this scope use the official ZYNX default template.',
    };
  }
  if (summary.status === 'published') {
    return {
      label: 'Published (Live)',
      tone: 'success',
      description: 'This custom template is live and actively used to generate documents.',
    };
  }
  return {
    label: 'Draft (Not Live)',
    tone: 'warning',
    description: 'Edits are saved as draft. Publish to use this template for generating documents.',
  };
}

interface TemplateScopeBarProps {
  activeDocLabel: string;
  department: string;
  onDepartmentChange: (dept: string) => void;
  summary: CustomTemplateSummary | undefined;
  hasCustom: boolean;
  canLoadStandard: boolean;
  onLoadStandard: () => void;
  onClone: () => void;
  onSave: () => void;
  onPublish: () => void;
  onResetConfirm: () => void;
  isSaving: boolean;
  isPublishing: boolean;
  isCloning: boolean;
  isReverting: boolean;
}

export function TemplateScopeBar({
  activeDocLabel,
  department,
  onDepartmentChange,
  summary,
  hasCustom,
  canLoadStandard,
  onLoadStandard,
  onClone,
  onSave,
  onPublish,
  onResetConfirm,
  isSaving,
  isPublishing,
  isCloning,
  isReverting,
}: TemplateScopeBarProps) {
  const [isCustomDept, setIsCustomDept] = useState(() => {
    return department !== '' && !STANDARD_DEPARTMENTS.some((d) => d.value === department);
  });
  const [customInput, setCustomInput] = useState(department);

  const status = statusFor(summary);

  function handleSelectChange(e: React.ChangeEvent<HTMLSelectElement>) {
    const val = e.target.value;
    if (val === '__custom__') {
      setIsCustomDept(true);
    } else {
      setIsCustomDept(false);
      onDepartmentChange(val);
    }
  }

  function handleCustomSubmit(val: string) {
    setCustomInput(val);
    onDepartmentChange(val.trim());
  }

  return (
    <div className="rounded-xl border border-outline bg-surface p-4 shadow-sm space-y-3">
      {/* Top row: Title + Scope selection + Status badge */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 pb-3 border-b border-outline/50">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary">
            <Building2 className="h-5 w-5" />
          </div>
          <div>
            <h2 className="text-base font-semibold text-on-surface">{activeDocLabel}</h2>
            <p className="text-xs text-muted-foreground flex items-center gap-1.5 mt-0.5">
              <span>Scope:</span>
              <span className="font-medium text-on-surface">
                {department ? department : 'Company-Wide Default'}
              </span>
            </p>
          </div>
        </div>

        {/* Scope dropdown & Custom indicator */}
        <div className="flex flex-wrap items-center gap-2">
          {!isCustomDept ? (
            <div className="relative">
              <select
                value={department}
                onChange={handleSelectChange}
                className="h-8 appearance-none rounded-lg border border-outline bg-surface-variant/40 px-3 pr-8 text-xs font-medium text-on-surface focus:outline-none focus:ring-2 focus:ring-primary/40 transition-colors cursor-pointer"
                aria-label="Select department scope"
              >
                {STANDARD_DEPARTMENTS.map((d) => (
                  <option key={d.value} value={d.value}>
                    {d.label}
                  </option>
                ))}
                <option value="__custom__">Custom Department...</option>
              </select>
              <ChevronDown className="absolute right-2.5 top-2.5 h-3.5 w-3.5 text-muted-foreground pointer-events-none" />
            </div>
          ) : (
            <div className="flex items-center gap-1.5">
              <input
                type="text"
                value={customInput}
                onChange={(e) => handleCustomSubmit(e.target.value)}
                placeholder="Enter department name..."
                className="h-8 w-48 rounded-lg border border-primary bg-surface px-2.5 text-xs text-on-surface focus:outline-none focus:ring-2 focus:ring-primary/40"
                autoFocus
              />
              <button
                type="button"
                onClick={() => {
                  setIsCustomDept(false);
                  onDepartmentChange('');
                }}
                className="text-[11px] text-muted-foreground hover:text-primary px-1 underline"
              >
                Reset
              </button>
            </div>
          )}

          {/* Status Badge */}
          <div title={status.description}>
            <Badge tone={status.tone} className="px-3 py-1 text-xs font-semibold">
              <span
                className={`h-1.5 w-1.5 rounded-full mr-1.5 ${
                  status.tone === 'success'
                    ? 'bg-success animate-pulse'
                    : status.tone === 'warning'
                    ? 'bg-warning'
                    : 'bg-slate-400'
                }`}
              />
              {status.label}
            </Badge>
          </div>
        </div>
      </div>

      {/* Action buttons toolbar */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        {/* Left: Template origin & standard loaders */}
        <div className="flex flex-wrap items-center gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={onLoadStandard}
            disabled={!canLoadStandard}
            className="text-xs border-outline hover:bg-surface-variant"
            title="Load ZYNX standard template into the editor"
          >
            <Wand2 className="h-3.5 w-3.5 mr-1.5 text-primary" />
            Load ZYNX Standard
          </Button>

          <Button
            size="sm"
            variant="outline"
            onClick={onClone}
            loading={isCloning}
            className="text-xs border-outline hover:bg-surface-variant"
            title="Clone standard template to a new editable draft"
          >
            <Wand2 className="h-3.5 w-3.5 mr-1.5 text-primary" />
            Clone to Draft
          </Button>

          {hasCustom && (
            <Button
              size="sm"
              variant="danger"
              onClick={onResetConfirm}
              loading={isReverting}
              className="text-xs border-danger/30 hover:bg-danger/10"
              title="Reset and delete custom template for this scope"
            >
              <RotateCcw className="h-3.5 w-3.5 mr-1.5" />
              Reset to Standard
            </Button>
          )}
        </div>

        {/* Right: Primary Save & Publish workflow */}
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={onSave}
            loading={isSaving}
            className="text-xs border-primary/50 text-primary hover:bg-primary/5 font-semibold"
            title="Save current edits as draft (Ctrl+S / ⌘S)"
          >
            <Save className="h-3.5 w-3.5 mr-1.5" />
            Save Draft
          </Button>

          <Button
            size="sm"
            variant="primary"
            onClick={onPublish}
            loading={isPublishing}
            className="text-xs bg-success text-white hover:bg-success/90 shadow-sm font-semibold"
            title="Publish template so it is used immediately for document generation"
          >
            <UploadCloud className="h-3.5 w-3.5 mr-1.5" />
            Publish Live
          </Button>
        </div>
      </div>
    </div>
  );
}
